/**
 * Video Aura — Three.js 依存部（ambilight 型の動画縁発光）
 *
 * anchor.group 追従のクアッド1枚（動画の spread 倍・動画の真裏 z=-0.001）に、
 * 動画テクスチャ自身を gaussian blur したブラー絵を加算合成で描く。
 * 濃度は動画エッジで最大・外周に向かって急峻に減衰するリング型で、
 * 「動画が発光して縁からにじむ」見え方を作る。動画背後はシェーダで完全クリア。
 *
 * 全単一マーカー案件でデフォルトON（CEO決定 2026-07-15・実機採用値 int=0.8）。
 * オプトアウトは posters.json の effects.aura.enabled: false。
 *
 * ノイズ合成（fbm モヤ・線香花火粒 等）は実機イテレーションで却下済み —
 * 全画面被覆はARの現実感を壊し、格子ノイズはドット絵化する（git 履歴参照）。
 *
 * 純ロジック（設定解決・フェード・レイアウト）は video-aura-logic.js に分離。
 */

import {
  AdditiveBlending,
  DoubleSide,
  GLSL3,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
  Vector2,
  Vector3,
} from 'three';
import { createFadeState, stepFade, computeSourceLayout } from './video-aura-logic.js';

const VERT = /* glsl */ `
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

// 動画テクスチャそのものをぼかして「動画のブラー絵」を周りに滲ませる ambilight 型実装。
// mipmap 使用禁止（iOS Safari ジッタ回帰 fd7d5ce）→ textureLod 不可なので、
// 9タップ gaussian blur を shader 側で明示的に組む。
//
// uCoverage は「ブラー強度」の意味に転用（0=シャープ〜1=強ブラー）。
// 従来の「モヤ被覆率」から意味変更。URL `?cov=` と posters.effects.aura.coverage の
// エンドポイントは変えず、値の意味だけ差し替えている。
const FRAG = /* glsl */ `
precision highp float;

uniform sampler2D uMap;
uniform float uTime;
uniform float uCoverage; // ブラー強度 0..1
uniform float uIntensity;
uniform vec3 uTint;
uniform float uFade;
uniform float uSpeed; // 呼吸（明滅）の速さ
uniform vec2 uInnerRadius; // 動画エッジまでの UV 半径（x/y）

in vec2 vUv;
out vec4 fragColor;

// sourceMesh の vUv (0..1) を動画テクスチャの UV (0..1) にマップする
vec2 mapToVideoUv(vec2 uv) {
  return (uv - 0.5) / (2.0 * uInnerRadius) + 0.5;
}

// 9タップ gaussian blur（3x3 kernel）。offsetは物理単位で渡す
vec3 sampleBlurred(vec2 videoUv, float r) {
  vec3 c = vec3(0.0);
  // 中心
  c += texture(uMap, clamp(videoUv, 0.0, 1.0)).rgb * 0.147761;
  // 十字
  c += texture(uMap, clamp(videoUv + vec2( r, 0.0), 0.0, 1.0)).rgb * 0.11831;
  c += texture(uMap, clamp(videoUv + vec2(-r, 0.0), 0.0, 1.0)).rgb * 0.11831;
  c += texture(uMap, clamp(videoUv + vec2(0.0,  r), 0.0, 1.0)).rgb * 0.11831;
  c += texture(uMap, clamp(videoUv + vec2(0.0, -r), 0.0, 1.0)).rgb * 0.11831;
  // 対角
  c += texture(uMap, clamp(videoUv + vec2( r,  r), 0.0, 1.0)).rgb * 0.0947416;
  c += texture(uMap, clamp(videoUv + vec2(-r,  r), 0.0, 1.0)).rgb * 0.0947416;
  c += texture(uMap, clamp(videoUv + vec2( r, -r), 0.0, 1.0)).rgb * 0.0947416;
  c += texture(uMap, clamp(videoUv + vec2(-r, -r), 0.0, 1.0)).rgb * 0.0947416;
  return c;
}

void main() {
  if (uFade < 0.01) {
    fragColor = vec4(0.0);
    return;
  }

  // 動画エッジ座標系: 動画中は k<1、動画エッジで k=1、外はどんどん大きく
  vec2 d = abs(vUv - 0.5);
  vec2 normalized = d / uInnerRadius;
  float k = max(normalized.x, normalized.y);

  // 動画背後は完全にクリア（動画本体を汚さない）
  float outside = smoothstep(0.98, 1.02, k);

  // 動画エッジで最も濃く、sourceMesh の短辺エッジまで減衰。
  // pow を強めにして縁のすぐ外で消える「動画が発光してる」ノリに留める
  // （ダラダラ延ばすとポスターを隠して AR として破綻する）
  float outerK = min(0.5 / uInnerRadius.x, 0.5 / uInnerRadius.y);
  float falloff = 1.0 - smoothstep(1.0, outerK, k);
  falloff = pow(falloff, 2.4);

  float radial = outside * falloff;

  // sourceMesh 座標 → 動画テクスチャ座標。外側では clamp で動画エッジ色が延びる
  vec2 videoUv = mapToVideoUv(vUv);

  // ブラー半径: cov 0..1 を UV 0.03..0.23 にマップ
  float blurR = 0.03 + uCoverage * 0.20;
  vec3 blurred = sampleBlurred(videoUv, blurR);
  vec3 tinted = blurred * uTint;

  // 発光の呼吸（speed 設定で速さを変えられる）
  float breathe = 0.9 + 0.1 * sin(uTime * 0.6 * uSpeed);
  float alpha = radial * uIntensity * uFade * breathe;

  fragColor = vec4(tinted * alpha, alpha);
}
`;

/**
 * オーラエフェクト（発生源1層）を生成する。
 *
 * @param {THREE.Texture} videoTexture - 共有動画テクスチャ（追加デコードなし）
 * @param {Object} config - resolveAuraConfig() の戻り値
 * @param {{width: number, height: number}} videoSize - 動画プレーンの anchor 単位サイズ
 *   （動画背後をシェーダーでクリアするために必要）
 * @returns {{
 *   sourceMesh: THREE.Mesh,
 *   update: (dtMs: number) => void,
 *   setActive: (on: boolean) => void,
 *   dispose: () => void,
 * }}
 */
export function createVideoAura(videoTexture, config, videoSize) {
  const layout = computeSourceLayout(config.spread);
  // 動画エッジまでの UV 半径: sourceMesh 中心から動画エッジまでを 0..1 で表す
  const innerRadius = new Vector2(
    videoSize.width / (2 * layout.width),
    videoSize.height / (2 * layout.height)
  );

  const uniforms = {
    uMap: { value: videoTexture },
    uTime: { value: 0 },
    uCoverage: { value: config.coverage },
    uIntensity: { value: config.intensity },
    uTint: { value: new Vector3(config.tint[0], config.tint[1], config.tint[2]) },
    uFade: { value: 0 },
    uSpeed: { value: config.speed },
    uInnerRadius: { value: innerRadius },
  };

  const material = new ShaderMaterial({
    glslVersion: GLSL3,
    vertexShader: VERT,
    fragmentShader: FRAG,
    uniforms,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    depthTest: true,
    side: DoubleSide,
  });

  const geometry = new PlaneGeometry(layout.width, layout.height);
  const sourceMesh = new Mesh(geometry, material);
  sourceMesh.frustumCulled = false;
  sourceMesh.renderOrder = 5;
  // 動画背後に配置（動画は z=0.01 で手前）。動画部分はシェーダーの outside=0 でクリアされ、
  // さらに動画の opaque コンテンツで上書きされる二重の保護
  sourceMesh.position.z = -0.001;

  const fadeState = createFadeState();
  let targetOn = false;
  let elapsedSec = 0;

  return {
    sourceMesh,

    update(dtMs) {
      if (!targetOn && fadeState.value === 0) return;

      const safeDt = Math.max(0, Math.min(dtMs, 100));
      elapsedSec += safeDt / 1000;
      stepFade(fadeState, targetOn, safeDt, config.fadeMs);
      uniforms.uTime.value = elapsedSec;
      uniforms.uFade.value = fadeState.value;

      sourceMesh.visible = fadeState.value > 0.001;
    },

    setActive(on) {
      targetOn = !!on;
    },

    dispose() {
      // 親から外す責務は dispose 側に集約（外部リセット処理の記述漏れを防ぐ）。
      // videoTexture は共有リソースのため dispose しない。
      sourceMesh.parent?.remove(sourceMesh);
      geometry.dispose();
      material.dispose();
    },
  };
}
